import { getMerchantBusinessAvatarCoffee } from '@assets/illustrations';

const DEFAULT_AVATAR = getMerchantBusinessAvatarCoffee();

const pickFirst = (...values) => values.find((value) => value != null && value !== '');

const asNonEmptyString = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

/** Reads the detail API `formatedAddress` object (API spelling). */
export const getFormatedAddress = (restaurant = {}) => {
  const value = pickFirst(
    restaurant.formatedAddress,
    restaurant.formattedAddress,
    restaurant.formatted_address,
  );
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
};

const asTrimmed = (value) => String(value ?? '').trim();

/** True when a string already looks like a complete formatted address. */
const isLikelyFullAddress = (value) => {
  const text = asTrimmed(value);
  if (!text) return false;
  return text.split(',').filter((part) => part.trim()).length >= 3;
};

/**
 * Joins street lines for display. Skips addressLine2 when both lines look like
 * full addresses (API sometimes stores a second complete address there).
 */
const joinStreetLines = (formatedAddress) => {
  const line1 = asTrimmed(formatedAddress?.addressLine1 ?? formatedAddress?.address_line_1);
  const line2 = asTrimmed(formatedAddress?.addressLine2 ?? formatedAddress?.address_line_2);
  if (!line1) return line2;
  if (!line2) return line1;
  if (isLikelyFullAddress(line1)) return line1;
  return `${line1}, ${line2}`;
};

const includesIgnoreCase = (haystack, needle) => {
  if (!haystack || !needle) return false;
  return haystack.toLowerCase().includes(String(needle).toLowerCase());
};

/** Builds a display address from the API `formatedAddress` object. */
export const formatRestaurantLocation = (restaurant = {}) => {
  const formatedAddress = getFormatedAddress(restaurant);
  const street = pickFirst(
    asNonEmptyString(restaurant.streetAddress),
    asNonEmptyString(restaurant.address),
    asNonEmptyString(joinStreetLines(formatedAddress)),
  );

  const parts = [];
  if (street) parts.push(street);

  const appendIfMissing = (value) => {
    const next = asNonEmptyString(value);
    if (!next) return;
    const current = parts.join(', ');
    if (includesIgnoreCase(current, next)) return;
    parts.push(next);
  };

  // When street is already a full address, do not re-append city/state/zip/country.
  if (!isLikelyFullAddress(street)) {
    appendIfMissing(pickFirst(restaurant.city, formatedAddress?.city));
    appendIfMissing(pickFirst(restaurant.state, formatedAddress?.state));
    appendIfMissing(
      pickFirst(
        restaurant.zipCode,
        restaurant.zipcode,
        restaurant.postalCode,
        formatedAddress?.zipcode,
        formatedAddress?.zipCode,
      ),
    );
    appendIfMissing(pickFirst(restaurant.country, formatedAddress?.country));
  }

  if (parts.length > 0) return parts.join(', ');

  return (
    pickFirst(
      asNonEmptyString(restaurant.location),
      asNonEmptyString(restaurant.formattedAddress),
      asNonEmptyString(restaurant.formatted_address),
    ) || ''
  );
};

const mapPriceTier = (restaurant = {}) => {
  const tier = pickFirst(
    restaurant.priceTier,
    restaurant.price,
    restaurant.priceForTwo,
    restaurant.priceLevel,
    restaurant.price_level,
  );

  if (tier == null || tier === '') return '1';

  if (typeof tier === 'number' || /^\d+$/.test(String(tier).trim())) {
    const numeric = Math.min(Math.max(Number(tier), 1), 4);
    return String(numeric);
  }

  const dollarCount = String(tier).replace(/[^$]/g, '').length;
  if (dollarCount > 0) return String(Math.min(dollarCount, 4));

  return '1';
};

/**
 * Normalizes POST /merchant/restaurant/detail response for profile UI.
 */
export const mapRestaurantDetail = (payload = {}) => {
  const restaurant = payload?.data ?? payload ?? {};
  const formatedAddress = getFormatedAddress(restaurant);

  const id = String(
    pickFirst(restaurant.id, restaurant.restaurantId, restaurant.placeId, restaurant.place_id) ||
      '',
  );

  return {
    id,
    placeId: pickFirst(restaurant.placeId, restaurant.place_id, id) || '',
    name:
      pickFirst(restaurant.name, restaurant.restaurantName, restaurant.businessName) || 'Business',
    location: formatRestaurantLocation(restaurant),
    avatar:
      pickFirst(
        restaurant.thumbnail,
        restaurant.avatar,
        restaurant.image,
        restaurant.imageUrl,
        restaurant.photoUrl,
        restaurant.profilePicUrl,
        restaurant.logoUrl,
      ) || DEFAULT_AVATAR,
    coverImage:
      pickFirst(
        restaurant.coverImage,
        restaurant.coverUrl,
        restaurant.bannerImage,
        restaurant.bannerUrl,
        restaurant.heroImage,
        restaurant.image,
        restaurant.imageUrl,
      ) || '',
    phone:
      pickFirst(
        restaurant.phone,
        restaurant.phoneNumber,
        restaurant.contactNumber,
        restaurant.mobileNumber,
      ) || '',
    countryCode:
      pickFirst(
        restaurant.countryCode,
        restaurant.country_code,
        restaurant.dialCode,
        restaurant.phoneCountryCode,
      ) || '+1',
    email: pickFirst(restaurant.email, restaurant.contactEmail, restaurant.businessEmail) || '',
    priceTier: mapPriceTier(restaurant),
    streetAddress:
      pickFirst(
        restaurant.streetAddress,
        restaurant.address,
        formatedAddress?.addressLine1,
        formatedAddress?.address_line_1,
      ) || '',
    addressLine2: pickFirst(formatedAddress?.addressLine2, formatedAddress?.address_line_2) || '',
    city: pickFirst(restaurant.city, restaurant.locationCity, formatedAddress?.city) || '',
    state:
      pickFirst(restaurant.state, restaurant.province, restaurant.region, formatedAddress?.state) ||
      '',
    zipCode:
      pickFirst(
        restaurant.zipCode,
        restaurant.postalCode,
        restaurant.zip,
        restaurant.zipcode,
        formatedAddress?.zipcode,
        formatedAddress?.zipCode,
        formatedAddress?.postalCode,
      ) || '',
    country: pickFirst(restaurant.country, restaurant.countryName, formatedAddress?.country) || '',
    zipCodeSuffix:
      pickFirst(formatedAddress?.zipCodeSuffix, formatedAddress?.zipcodeSuffix) || null,
    latitude: (() => {
      const value = Number(pickFirst(restaurant.latitude, restaurant.lat));
      return Number.isFinite(value) ? value : null;
    })(),
    longitude: (() => {
      const value = Number(pickFirst(restaurant.longitude, restaurant.lng, restaurant.lon));
      return Number.isFinite(value) ? value : null;
    })(),
    orderLink:
      pickFirst(
        restaurant.orderLink,
        restaurant.orderUrl,
        restaurant.orderingLink,
        restaurant.orderingUrl,
      ) || '',
    reservationLink:
      pickFirst(
        restaurant.reservationLink,
        restaurant.reservationUrl,
        restaurant.bookingLink,
        restaurant.bookingUrl,
      ) || '',
    status: pickFirst(restaurant.status, restaurant.approvalStatus) || '',
    photos: Array.isArray(restaurant.photos) ? restaurant.photos : [],
    menuPhotos: Array.isArray(restaurant.menuPhotos)
      ? restaurant.menuPhotos
      : Array.isArray(restaurant.menuDetail?.menuPhotos)
        ? restaurant.menuDetail.menuPhotos
        : [],
    raw: restaurant,
  };
};

export default mapRestaurantDetail;
